/* eslint-disable no-param-reassign */
import { isPlainObject, memoize, transform } from 'lodash'
import { BoxProps } from '~/design_system'

const CANVAS_ATTR = '[data-canvas-theme]'
const THEME_BASE = '$base'
const THEME_CANVAS = '$canvas'

type SxObject = Record<string, unknown>

type ThemeHalves<V> = { [THEME_BASE]?: V; [THEME_CANVAS]?: V }
type WithThemeHalves<T> = { [K in keyof T]?: T[K] | ThemeHalves<T[K]> }
export type ThmStyles = WithThemeHalves<NonNullable<BoxProps['sx']>>

/**
 * Resolve a stack of nested `&`-selectors the way stylis would (inner `&` → parent selector).
 * e.g. `['& > .foo', '& > .bar']` → `'& > .foo > .bar'`
 */
function resolveSelectorPath(path: string[]): string {
  return path.reduce((acc, selector) => (acc ? selector.replace(/&/g, acc) : selector), '')
}

function canvasSelectorForPath(path: string[]): string {
  if (path.length === 0) return `${CANVAS_ATTR} &`
  return `${CANVAS_ATTR} ${resolveSelectorPath(path)}`
}

/**
 * Split `$base` / `$canvas` from any remaining Chakra/sx keys.
 * Mixing `$canvas` with Chakra breakpoints is allowed, unlike `cq()`.
 * Objects without those keys are left alone.
 */
function splitThemeValue(value: SxObject): { remainder: unknown; canvas: unknown; hasCanvas: boolean } | null {
  const hasCanvas = Object.prototype.hasOwnProperty.call(value, THEME_CANVAS)
  const hasBase = Object.prototype.hasOwnProperty.call(value, THEME_BASE)
  if (!hasCanvas && !hasBase) return null

  const { [THEME_CANVAS]: canvas, [THEME_BASE]: themeBase, ...rest } = value
  const restKeys = Object.keys(rest)

  let remainder: unknown
  if (hasBase && restKeys.length === 0) {
    // `{ $base: X }` or `{ $base: X, $canvas: Y }` → plain legacy value
    remainder = themeBase
  } else if (hasBase) {
    // `{ $base: X, sm: Y, … }` → map $base onto Chakra `base` in the responsive object
    remainder = { base: themeBase, ...rest }
  } else if (restKeys.length > 0) {
    // `{ base/sm/…, $canvas: Y }` → keep Chakra responsive remainder
    remainder = rest
  } else {
    // `{ $canvas: Y }` only
    remainder = undefined
  }

  return { remainder, canvas, hasCanvas }
}

function mergeHoisted(target: SxObject, source: SxObject): void {
  Object.entries(source).forEach(([selector, styles]) => {
    if (!isPlainObject(styles)) return
    target[selector] = {
      ...((target[selector] as SxObject) ?? {}),
      ...(styles as SxObject)
    }
  })
}

function mergeSx(sx: SxObject, hoisted: SxObject): SxObject {
  const result = { ...sx }
  mergeHoisted(result, hoisted)
  return result
}

/**
 * sx preprocessor for the canvas theme body attribute.
 *
 * Normal sx plus theme halves: `$base` (legacy) and `$canvas` (under
 * `[data-canvas-theme]`). `$base` is named that way so it does not collide with
 * Chakra's `base` breakpoint.
 *
 * Nested `&` selectors hoist `$canvas` overrides to flat prefixed keys. A nested
 * `'[data-canvas-theme] &': { 'ancestor &': … }` is a broken stylis nest.
 *
 * @example
 * ```tsx
 * sx={$thm({
 *   color: 'red',
 *   px: { base: 6, sm: 8, $canvas: 0 },
 *   '*:is([data-card], [data-card-body]) > [data-items-list] &': {
 *     borderRadius: { $base: 'inherit', $canvas: 'lg' }
 *   }
 * })}
 * ```
 */
export const $thm = memoize(
  function $thm(styles: ThmStyles | undefined): BoxProps['sx'] {
    if (!styles || !isPlainObject(styles)) return undefined

    function modify(scope: SxObject, path: string[]): { sx: SxObject; hoisted: SxObject } {
      const hoisted: SxObject = {}

      const sx = transform(
        scope,
        (result: SxObject, value: unknown, key: string | number) => {
          const keyStr = String(key)

          if (!isPlainObject(value)) {
            result[key] = value
            return
          }

          const valueObj = value as SxObject
          const split = splitThemeValue(valueObj)

          if (split) {
            if (split.remainder !== undefined) {
              result[key] = split.remainder
            }
            if (split.hasCanvas) {
              const selector = canvasSelectorForPath(path)
              hoisted[selector] = {
                ...((hoisted[selector] as SxObject) ?? {}),
                [key]: split.canvas
              }
            }
            return
          }

          // Nested `$thm()` output already emitted `[data-canvas-theme] …` keys.
          // Merge them. Treating them as a new `&` nest re-prefixes, then the
          // final spread overwrites the same key.
          if (keyStr.startsWith(CANVAS_ATTR)) {
            mergeHoisted(hoisted, { [keyStr]: valueObj })
            return
          }

          if (keyStr.includes('&')) {
            const nested = modify(valueObj, [...path, keyStr])
            if (Object.keys(nested.sx).length > 0) {
              result[key] = nested.sx
            }
            mergeHoisted(hoisted, nested.hoisted)
            return
          }

          // `_hover` / responsive objects: reset the hoist path so nested $canvas
          // uses local `[data-canvas-theme] &` and the parent selector still applies.
          const nested = modify(valueObj, [])
          result[key] = mergeSx(nested.sx, nested.hoisted)
        },
        {} as SxObject
      )

      return { sx, hoisted }
    }

    const { sx, hoisted } = modify(styles as SxObject, [])
    return mergeSx(sx, hoisted)
  },
  (styles: ThmStyles | undefined) => stableHash(styles)
)

function stableHash(obj: ThmStyles | undefined): string {
  return JSON.stringify(obj, (_, val) =>
    isPlainObject(val)
      ? Object.keys(val)
          .sort()
          .reduce((result, key) => {
            result[key] = val[key]
            return result
          }, {} as SxObject)
      : val
  )
}
